import { useMemo } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash-es';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Chip, Avatar, NakedButton, Text, ContentLoader } from '@frontend/design-system';
import { DATETIME_FORMATS } from '../../../../constants';
import { useBookingSiteStore } from '../../../../stores/use-booking-site-store';
import { BOOKING_SITE_TRACKING_IDS } from '../../../../trackingIds/booking-site-tracking-ids';
import { BOOKING_SITE_HTTP_OPTIONS } from '../../../../utils';
import { endOfWeek } from '../../../../utils/date-helpers';
import { AllProvidersApptCount, ProviderApptCount } from './provider-appt-count';

const getValidFutureNextAvailableDate = (nextAvailableDate?: string, selectedLocationTimeZone?: string) => {
  if (!nextAvailableDate || !selectedLocationTimeZone) {
    return null;
  }

  const nextAvailableDay = dayjs.tz(`${nextAvailableDate}T00:00:00`, selectedLocationTimeZone);
  const todayInLocation = dayjs().tz(selectedLocationTimeZone).startOf('day');

  if (!nextAvailableDay.isValid() || !nextAvailableDay.isAfter(todayInLocation)) {
    return null;
  }

  return nextAvailableDay;
};

export const ProviderList = () => {
  const { t } = useTranslation('bookingSite');
  const {
    isSchedularV3Enabled,
    availableProviders,
    setSelectedProvider,
    allProviderAvailabilities,
    isScheduleDefinitionsEnabled,
    selectedLocationId,
    selectedLocationTimeZone,
    selectedAppointmentType,
    weekInView,
  } = useBookingSiteStore([
    'isSchedularV3Enabled',
    'availableProviders',
    'setSelectedProvider',
    'allProviderAvailabilities',
    'isScheduleDefinitionsEnabled',
    'selectedLocationId',
    'selectedLocationTimeZone',
    'selectedAppointmentType',
    'weekInView',
  ]);

  const { startDateTime, endDateTime } = useMemo(() => {
    const weekDate = dayjs(weekInView).tz(selectedLocationTimeZone);

    return {
      startDateTime: weekDate.startOf('day').format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET),
      endDateTime: endOfWeek(weekInView, selectedLocationTimeZone).format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET),
    };
  }, [selectedLocationTimeZone, weekInView]);

  const bookableSlotsQuery = SchedulerV3.Queries.useListBookableAppointmentSlotsQuery({
    request: {
      startDateTime,
      endDateTime,
      locationId: selectedLocationId,
      providerIds: availableProviders.map(({ id }) => id || ''),
      bookableAppointmentTypeId: selectedAppointmentType?.bookableAppointmentTypeId ?? '',
      slotDuration: selectedAppointmentType?.durationMinutes ?? 10,
    },
    options: { enabled: isScheduleDefinitionsEnabled && !!selectedLocationId },
    httpOptions: BOOKING_SITE_HTTP_OPTIONS,
    bypassScheduleV3FlagCheck: true,
  });

  const isLoadingProviderSlots = isEmpty(allProviderAvailabilities);
  return (
    <section css={containerStyle}>
      {!isSchedularV3Enabled && availableProviders.length > 1 && (
        <NakedButton
          onClick={() => setSelectedProvider('any')}
          trackingId={BOOKING_SITE_TRACKING_IDS.providerStep.noPreferenceButton}
        >
          <div className='provider-info'>
            <Avatar src={''} size='xl' />
            <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
              <Text as='span' weight='bold' css={{ textAlign: 'start' }}>
                {t('No Preference')}
              </Text>
              <Chip css={{ maxWidth: 'max-content' }}>{t('Most Availability')}</Chip>
            </div>
          </div>
          <AllProvidersApptCount />
        </NakedButton>
      )}
      <ContentLoader show={isLoadingProviderSlots} />
      {availableProviders.map((provider) => {
        const { firstName, lastName, publicDisplayName, publicDisplayImage, id } = provider;
        const name = publicDisplayName || `${firstName} ${lastName}`;
        const providerSlots = bookableSlotsQuery.data?.providerSlots?.find((slot) => slot.providerId === id);
        const nextAvailableDay = getValidFutureNextAvailableDate(
          providerSlots?.nextAvailableDate,
          selectedLocationTimeZone
        );
        const hasCurrentWeekAvailability = !!(id && allProviderAvailabilities?.[id]?.length > 0);
        const hasFutureNextAvailableDate = !!nextAvailableDay;
        const shouldShowProvider =
          !isScheduleDefinitionsEnabled || hasCurrentWeekAvailability || hasFutureNextAvailableDate;

        return (
          <NakedButton
            key={id}
            onClick={() => setSelectedProvider(provider)}
            trackingId={BOOKING_SITE_TRACKING_IDS.providerStep.providerSelect}
            style={{
              display: shouldShowProvider ? 'flex' : 'none',
            }}
          >
            <div className='provider-info'>
              <Avatar name={name} src={publicDisplayImage} size='xl' />
              <Text
                as='span'
                weight='bold'
                textAlign='left'
                css={{ overflowWrap: 'break-word', wordBreak: 'break-word', whiteSpace: 'normal' }}
              >
                {name}
              </Text>
            </div>
            <ProviderApptCount
              provider={provider}
              providerSlots={providerSlots}
              nextAvailableDay={nextAvailableDay}
              isBookableSlotsLoading={bookableSlotsQuery.isLoading}
            />
          </NakedButton>
        );
      })}
    </section>
  );
};

const containerStyle = css({
  position: 'relative',
  minHeight: 120,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '100%',
  button: {
    minWidth: '100%',
    borderBottom: `1px solid ${theme.colors.neutral20}`,
    display: 'flex',
    justifyContent: 'space-between',
    '.provider-info': {
      alignSelf: 'center',
      minWidth: 250,
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(2, 0),
      gap: theme.spacing(2),
    },
    '@media only screen and (max-width: 650px)': {
      flexDirection: 'column',
      '.provider-info': {
        position: 'sticky',
        left: 0,
        right: 0,
      },
    },
  },
});
